// Standardized copy for every "sync a tier's config down to staff" moment.
//
// One source of truth so the wording + options are identical across the
// originating events (add a staff member, move a member to a DIFFERENT tier,
// reassign members while deleting a tier) and the defaults editors' "apply to
// members" prompt. Unparenting (moving to "No tier") is intentionally NOT a sync
// event: it leaves the member's services/commissions untouched and shows no
// prompt.

export const TIER_SYNC_COPY = {
  /** Modal intro. `subject` is a staff name (single) or a phrase like
   * "the selected staff members" (bulk). */
  helpText: (tierName: string, subject: string): string =>
    `Do you want to apply ${tierName} default tier configurations to ${subject}?`,

  /** The "replace service list" option — offered only when the destination tier
   * has service defaults. `who` is already possessive (e.g. "Amy's" or
   * "the selected staff members'"). */
  serviceOptionLabel: (who: string): string =>
    `Overwrite ${who} service list & custom configurations with this tier's default service list`,
  serviceOptionTooltip:
    "This action may add new services, overwrite existing service pricing/durations and remove any services that are not in the tier's service list.",

  /** The "apply commission structure" option — offered only when the tier has a
   * commission default. */
  commissionOptionLabel: (who: string): string =>
    `Overwrite ${who} existing commission structure with the tier's default commission structure`,
  commissionOptionTooltip:
    "This action will apply the tier's default commission structure effective starting today and make any previous commission structure inactive",

  /** Required acknowledgement — gates the confirm button everywhere. Rendered
   * with a trailing red asterisk. */
  acknowledgeLabel:
    "I understand that these changes may impact existing staff settings and cannot easily be undone",

  /** Shown when a tier defines no defaults at all (nothing to sync). */
  noDefaults: "This tier has no service or commission defaults to apply.",
} as const;

/** Human phrase for how many staff a sync will touch (non-possessive). */
export function syncSubject(count: number): string {
  return count === 1 ? "this staff member" : `these ${count} staff members`;
}

/** Possessive form of a subject phrase/name ("Amy" → "Amy's"; "…members" →
 * "…members'"). */
export function possessive(subject: string): string {
  return subject.endsWith("s") ? `${subject}'` : `${subject}'s`;
}
